import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook for URL-synced pagination
 *
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Number of items per page (default: 10)
 * @param {string} options.pageParam - URL query parameter name (default: 'page')
 * @returns {Object} Pagination state and helpers
 */
export function usePagination({ pageSize = 10, pageParam = 'page' } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read current page from URL, default to 1
  const currentPage = parseInt(searchParams.get(pageParam) || '1', 10);

  // Track total pages from API response
  const [totalPages, setTotalPages] = useState(1);

  // Track displayed pagination info separately to avoid race condition
  // These only update AFTER data loads successfully
  const [displayedPage, setDisplayedPage] = useState(1);
  const [displayedTotalCount, setDisplayedTotalCount] = useState(0);

  /**
   * Update the current page in the URL
   * Page 1 removes the query param for cleaner URLs
   */
  const setCurrentPage = useCallback((page) => {
    const newParams = new URLSearchParams(searchParams);
    if (page === 1) {
      newParams.delete(pageParam);
    } else {
      newParams.set(pageParam, page.toString());
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, pageParam]);

  /**
   * Update pagination state after successful data load
   * Call this in your data fetching function after receiving the response
   *
   * @param {number} totalCount - Total number of items from API
   */
  const updatePaginationFromResponse = useCallback((totalCount) => {
    setTotalPages(Math.ceil(totalCount / pageSize));
    setDisplayedPage(currentPage);
    setDisplayedTotalCount(totalCount);
  }, [currentPage, pageSize]);

  /**
   * Reset pagination to page 1 (useful when filters/sorting change)
   */
  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);

  /**
   * Calculate the range of items being displayed
   * @returns {Object} { start, end } - 1-indexed range
   */
  const getDisplayRange = useCallback(() => {
    const start = ((displayedPage - 1) * pageSize) + 1;
    const end = Math.min(displayedPage * pageSize, displayedTotalCount);
    return { start, end };
  }, [displayedPage, pageSize, displayedTotalCount]);

  return {
    // Current state
    currentPage,
    totalPages,
    pageSize,
    displayedPage,
    displayedTotalCount,

    // Actions
    setCurrentPage,
    updatePaginationFromResponse,
    resetToFirstPage,

    // Helpers
    getDisplayRange,

    // Computed values
    hasPreviousPage: currentPage > 1,
    hasNextPage: currentPage < totalPages,
    showPagination: totalPages > 1,
  };
}

export default usePagination;
